import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import OdometroScanner from './OdometroScanner';
import { VIN_TABLE } from '../vinTable';

const API = 'https://green-energy.com.co/api';

const DEFAULT_BUS = { zonal: 'Desconocido', placa: '—', marca: '—', cop: '—' };

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background:'#1a1d27', border:'1px solid #2a2d3a', padding:'10px', borderRadius:'8px', fontSize:'12px' }}>
        <p style={{ color:'#aaa', marginBottom:'6px' }}>{label}</p>
        {payload.map(p => (
          <p key={p.dataKey} style={{ color:p.color, margin:'2px 0' }}>
            {p.name}: <strong>{p.value} {p.dataKey==='soc'?'%':p.dataKey==='power'?'kW':p.dataKey==='voltage'?'V':p.dataKey==='current'?'A':'kWh'}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function SessionDetail({ txId, onClose }) {
  const [session, setSession] = useState(null);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('potencia');
  const [odometro, setOdometro] = useState('');
  const [odometroGuardado, setOdometroGuardado] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    // Cargar sesión real de la API
    fetch(`${API}/transactions`)
      .then(r => r.json())
      .then(data => {
        const tx = data.find(t => (t.tx_id || t.txId) === txId);
        if (tx) {
          setSession({
            txId: tx.tx_id || tx.txId,
            cpId: tx.cp_id || tx.cpId,
            cop: tx.cop || 'Usme',
            idTag: tx.id_tag || tx.idTag || 'AUTOSTART',
            vin: tx.vin || null,
            zonal: tx.zonal || null,
            startTime: tx.start_time || tx.startTime,
            stopTime: tx.stop_time || tx.stopTime,
            meterStart: tx.meter_start || tx.meterStart || 0,
            meterStop: tx.meter_stop || tx.meterStop || 0,
            energyDelivered: tx.energy_delivered || tx.energyDelivered || 0,
            status: tx.status || 'Active',
            connector: tx.connector_id || tx.connectorId || 1,
            socStart: tx.soc_start || tx.socStart || 0,
            socEnd: tx.soc_end || tx.socEnd || 0,
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Cargar lecturas de medidor
    fetch(`${API}/metervalues?txId=${txId}`)
      .then(r => r.json())
      .then(data => {
        const filtered = data
          .filter(r => (r.tx_id || r.txId) === txId)
          .sort((a,b) => new Date(a.ts) - new Date(b.ts))
          .map(r => ({
            hora: new Date(r.ts).toTimeString().slice(0,5),
            soc: r.soc || 0,
            power: parseFloat((r.power||0).toFixed(1)),
            voltage: r.voltage || 0,
            current: r.current || 0,
            energy: r.energy ? parseFloat(((r.energy - (data[0]?.energy||r.energy))/1000).toFixed(2)) : 0,
          }));
        setReadings(filtered);
      })
      .catch(() => {});
  }, [txId]);

  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#fff', fontSize:'16px' }}>Cargando sesión...</div>
    </div>
  );

  if (!session) return null;

  // Buscar bus por VIN real o por idTag
  const vinKey = session.vin || session.idTag;
  const busFromTable = VIN_TABLE[vinKey] || DEFAULT_BUS;
  const bus = {
    vin: session.vin || session.idTag,
    zonal: session.zonal || busFromTable.zonal || '—',
    placa: busFromTable.placa || '—',
    marca: busFromTable.marca || '—',
    cop: busFromTable.cop || session.cop || '—',
  };

  const duracion = session.startTime && session.stopTime
    ? Math.round((new Date(session.stopTime) - new Date(session.startTime)) / 60000)
    : 0;
  const pad = n => String(n).padStart(2,'0');
  const durStr = d => d >= 60 ? `${Math.floor(d/60)}h ${pad(d%60)}m` : d > 0 ? `${d}m` : '—';
  const formatTime = ts => ts ? new Date(ts).toLocaleTimeString('es-CO') : '—';

  const exportExcel = () => {
    const wsData = [
      ['REPORTE DE SESIÓN DE CARGA — MUEVE'], [],
      ['ID Transacción', session.txId], ['Cargador', session.cpId], ['COP', session.cop],
      ['VIN', bus.vin], ['Zonal', bus.zonal], ['Placa', bus.placa], ['Marca', bus.marca],
      ['Odómetro (km)', odometro || '—'],
      ['Inicio', formatTime(session.startTime)],
      ['Fin', formatTime(session.stopTime)],
      ['Duración', durStr(duracion)],
      ['Energía entregada (kWh)', session.energyDelivered],
      ['SoC inicial (%)', session.socStart],
      ['SoC final (%)', session.socEnd],
      ['Conector', session.connector], [],
      ['LECTURAS MINUTO A MINUTO'],
      ['Hora','SoC (%)','Potencia (kW)','Voltaje (V)','Corriente (A)','Energía acum. (kWh)'],
      ...readings.map(r => [r.hora, r.soc, r.power, r.voltage, r.current, r.energy])
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{wch:25},{wch:15},{wch:15},{wch:12},{wch:15},{wch:18}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sesión');
    const buf = XLSX.write(wb, { bookType:'xlsx', type:'array' });
    saveAs(new Blob([buf], {type:'application/octet-stream'}), `sesion_${session.txId}_${bus.zonal}.xlsx`);
  };

  const tabs = [
    { id:'potencia', label:'Potencia (kW)' },
    { id:'soc', label:'SoC (%)' },
    { id:'voltaje', label:'Voltaje (V)' },
    { id:'corriente', label:'Corriente (A)' },
    { id:'energia', label:'Energía acum.' },
  ];
  const chartConfig = {
    potencia:  { key:'power',   color:'#60a5fa', name:'Potencia kW' },
    soc:       { key:'soc',     color:'#4ade80', name:'SoC %' },
    voltaje:   { key:'voltage', color:'#f59e0b', name:'Voltaje V' },
    corriente: { key:'current', color:'#a78bfa', name:'Corriente A' },
    energia:   { key:'energy',  color:'#34d399', name:'Energía kWh' },
  };
  const cc = chartConfig[activeTab];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ background:'#13151f', border:'1px solid #2a2d3a', borderRadius:'12px', width:'100%', maxWidth:'900px', maxHeight:'90vh', overflowY:'auto', padding:'1.5rem' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem' }}>
          <div>
            <div style={{ fontSize:'18px', fontWeight:'600', color:'#fff' }}>Detalle de sesión #{session.txId}</div>
            <div style={{ fontSize:'13px', color:'#666', marginTop:'3px' }}>{session.cop} · Cargador {session.cpId} · Conector {session.connector}</div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={exportExcel} style={{ padding:'7px 14px', background:'#1d6a3a', border:'none', borderRadius:'7px', color:'#fff', cursor:'pointer', fontSize:'13px' }}>⬇ Exportar Excel</button>
            {onClose && <button onClick={onClose} style={{ padding:'7px 14px', background:'#2a2d3a', border:'none', borderRadius:'7px', color:'#fff', cursor:'pointer', fontSize:'13px' }}>✕ Cerrar</button>}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1.25rem' }}>
          <div className="card" style={{ margin:0 }}>
            <div className="card-title">🚌 Identificación del bus</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'13px' }}>
              <span style={{ color:'#aaa' }}>VIN</span><span style={{ fontFamily:'monospace', fontSize:'11px' }}>{bus.vin}</span>
              <span style={{ color:'#aaa' }}>Zonal</span><span style={{ fontWeight:'600', color:'#4ade80' }}>{bus.zonal}</span>
              <span style={{ color:'#aaa' }}>Placa</span><span>{bus.placa}</span>
              <span style={{ color:'#aaa' }}>Marca</span><span>{bus.marca}</span>
              <span style={{ color:'#aaa' }}>COP</span><span>{bus.cop}</span>
              <span style={{ color:'#aaa' }}>Odómetro</span>
              <span style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                {odometroGuardado ? (
                  <span style={{ fontWeight:'600', color:'#60a5fa' }}>{parseInt(odometro).toLocaleString()} km ✓</span>
                ) : (
                  <>
                    <input type="number" value={odometro} onChange={e=>setOdometro(e.target.value)} placeholder="km"
                      style={{ width:'80px', padding:'3px 6px', background:'#0f1117', border:'1px solid #2a2d3a', borderRadius:'5px', color:'#fff', fontSize:'12px' }} />
                    <button onClick={async()=>{
  await fetch(`${API}/transactions/${session.txId}/odometro`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({odometro:parseInt(odometro)})});
  setOdometroGuardado(true);
}}
                      style={{ padding:'3px 8px', background:'#1d6a3a', border:'none', borderRadius:'5px', color:'#fff', cursor:'pointer', fontSize:'11px' }}>
                      Guardar
                    </button>
                    <button onClick={()=>setShowScanner(true)}
                      style={{ padding:'3px 8px', background:'#1a2a3a', border:'1px solid #60a5fa', borderRadius:'5px', color:'#60a5fa', cursor:'pointer', fontSize:'11px' }}>
                      📸 Escanear
                    </button>
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="card" style={{ margin:0 }}>
            <div className="card-title">⚡ Resumen de carga</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'13px' }}>
              <span style={{ color:'#aaa' }}>Inicio</span><span>{formatTime(session.startTime)}</span>
              <span style={{ color:'#aaa' }}>Fin</span><span>{formatTime(session.stopTime)}</span>
              <span style={{ color:'#aaa' }}>Duración</span><span style={{ fontWeight:'600' }}>{durStr(duracion)}</span>
              <span style={{ color:'#aaa' }}>Energía entregada</span><span style={{ fontWeight:'600', color:'#4ade80' }}>{session.energyDelivered} kWh</span>
              <span style={{ color:'#aaa' }}>SoC inicio → fin</span>
              <span><span style={{ color:'#f59e0b' }}>{session.socStart}%</span> → <span style={{ color:'#4ade80' }}>{session.socEnd}%</span></span>
              <span style={{ color:'#aaa' }}>Estado</span>
              <span style={{ color: session.status==='Completed'?'#4ade80':'#60a5fa' }}>{session.status==='Completed'?'Completada':'En carga'}</span>
            </div>
          </div>
        </div>

                <div className="card" style={{ margin:0 }}>
          {readings.length > 0 ? (
            <>
              <div style={{ display:'flex', gap:'6px', marginBottom:'1rem', flexWrap:'wrap' }}>
                {tabs.map(t => (
                  <button key={t.id} onClick={()=>setActiveTab(t.id)}
                    style={{ padding:'5px 12px', border:'1px solid', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'500',
                      background: activeTab===t.id?'#1d6a3a':'#1a1d27',
                      borderColor: activeTab===t.id?'#4ade80':'#2a2d3a',
                      color: activeTab===t.id?'#4ade80':'#aaa' }}>
                    {t.label}
                  </button>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={readings}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                  <XAxis dataKey="hora" tick={{ fill:'#666', fontSize:11 }} interval={Math.floor(readings.length/8)} />
                  <YAxis tick={{ fill:'#666', fontSize:11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize:'12px', color:'#aaa' }} />
                  <Line type="monotone" dataKey={cc.key} stroke={cc.color} strokeWidth={2} dot={false} name={cc.name} />
                  {activeTab==='potencia' && <Line type="monotone" dataKey={()=>135} stroke="#f87171" strokeWidth={1} strokeDasharray="5 4" dot={false} name="Límite 135 kW" />}
                  {activeTab==='soc' && <Line type="monotone" dataKey={()=>90} stroke="#f87171" strokeWidth={1} strokeDasharray="5 4" dot={false} name="Límite 90%" />}
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div style={{ textAlign:'center', padding:'2rem', color:'#555' }}>Sin lecturas de medidor para esta sesión</div>
          )}
        </div>
      </div>
      {showScanner && (
        <OdometroScanner busPlaca={bus.placa} busId={bus.zonal}
          onGuardar={async({km})=>{ 
            setOdometro(km); 
            await fetch(`${API}/transactions/${session.txId}/odometro`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({odometro:parseInt(km)})});
            setOdometroGuardado(true); 
            setShowScanner(false); 
          }}
          onClose={()=>setShowScanner(false)} />
      )}
    </div>
  );
}